import type { SandboxDashboardGpuUnits, SandboxDashboardState } from '@angular-workspace/services';

export const SANDBOX_DASHBOARD_TIMEZONE = 'Asia/Kolkata';

/** IST is a fixed UTC+05:30 offset (no DST). */
const IST_OFFSET_MS = 330 * 60 * 1000;
const DAY_MS = 24 * 60 * 60 * 1000;

export interface SandboxDashboardPeriodOption {
  label: string;
  /** Number of IST calendar days, including today. Must stay ≤ 30 (API window limit). */
  days: number;
}

export const SANDBOX_DASHBOARD_PERIODS: SandboxDashboardPeriodOption[] = [
  { label: 'Today', days: 1 },
  { label: 'Last 7 days', days: 7 },
  { label: 'Last 14 days', days: 14 },
  { label: 'Last 30 days', days: 30 },
];

/** Display order for the sandbox state breakdown. */
export const SANDBOX_STATE_ORDER: SandboxDashboardState[] = [
  'running',
  'stopped',
  'opening',
  'failed',
  'orphaned',
  'unknown',
];

/**
 * Window covering the last `days` IST calendar days up to now.
 * `from` is inclusive (IST midnight), `to` is exclusive and never later than now.
 */
export function buildIstWindow(days: number, now: Date = new Date()): { from: string; to: string } {
  const safeDays = Math.min(30, Math.max(1, Math.floor(days)));
  const istNow = now.getTime() + IST_OFFSET_MS;
  const istMidnightAsUtc = istNow - (istNow % DAY_MS);
  const from = istMidnightAsUtc - IST_OFFSET_MS - (safeDays - 1) * DAY_MS;
  return { from: new Date(from).toISOString(), to: now.toISOString() };
}

const dayMonthFormatter = new Intl.DateTimeFormat('en-GB', {
  timeZone: SANDBOX_DASHBOARD_TIMEZONE,
  day: 'numeric',
  month: 'short',
});

/** "6 Oct" — used for chart buckets. */
export function formatIstDayLabel(iso: string): string {
  const d = iso ? new Date(iso) : null;
  return d && !Number.isNaN(d.getTime()) ? dayMonthFormatter.format(d) : '';
}

export function isFiniteNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

/** Up to two decimals, trailing zeros dropped; `—` for unavailable values. */
export function formatSandboxNumber(value: number | null | undefined): string {
  if (!isFiniteNumber(value)) return '—';
  return new Intl.NumberFormat('en-IN', { maximumFractionDigits: 2 }).format(value);
}

export function formatSandboxHours(value: number | null | undefined): string {
  return isFiniteNumber(value) ? `${formatSandboxNumber(value)} h` : '—';
}

export function formatSandboxCores(value: number | null | undefined): string {
  if (!isFiniteNumber(value)) return '—';
  return `${formatSandboxNumber(value)} ${value === 1 ? 'core' : 'cores'}`;
}

export function formatSandboxBytes(bytes: number | null | undefined): string {
  if (!isFiniteNumber(bytes)) return '—';
  const gib = bytes / 1024 ** 3;
  if (gib >= 1 || bytes === 0) {
    return `${new Intl.NumberFormat('en-IN', { maximumFractionDigits: 1 }).format(gib)} GiB`;
  }
  const mib = bytes / 1024 ** 2;
  return `${new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 }).format(mib)} MiB`;
}

/** "2 nvidia.com/gpu" (grouped by resource name); "0" when nothing is requested. */
export function formatSandboxGpuUnits(resources: SandboxDashboardGpuUnits[] | null | undefined): string {
  if (resources == null) return '—';
  const totals = new Map<string, number>();
  for (const r of resources) {
    if (!r?.resourceName || !isFiniteNumber(r.units)) continue;
    totals.set(r.resourceName, (totals.get(r.resourceName) ?? 0) + r.units);
  }
  if (totals.size === 0) return '0';
  return Array.from(totals, ([name, units]) => `${formatSandboxNumber(units)} ${name}`).join(', ');
}

export function sandboxStateLabel(state: string): string {
  return state ? state.charAt(0).toUpperCase() + state.slice(1) : 'Unknown';
}

/** Maps a state to the badge tone used in styles (`sbd-state--<tone>`). */
export function sandboxStateTone(state: string): 'success' | 'warning' | 'danger' | 'neutral' {
  switch (state) {
    case 'running':
      return 'success';
    case 'opening':
    case 'orphaned':
      return 'warning';
    case 'failed':
      return 'danger';
    default:
      return 'neutral';
  }
}

/** Human-readable message for a failed dashboard request. */
export function sandboxDashboardErrorMessage(error: unknown, fallback: string): string {
  const err = error as { status?: number; error?: { detail?: string; title?: string } } | null;
  switch (err?.status) {
    case 401:
      return 'Your session has expired. Please sign in again.';
    case 403:
      return 'You need the platform admin role to view sandbox usage.';
    case 429:
      return 'Too many requests. Please wait a moment and try again.';
    case 503:
      return 'The sandbox service is temporarily unavailable.';
  }
  const detail = err?.error?.detail || err?.error?.title;
  return typeof detail === 'string' && detail.trim() ? detail.trim() : fallback;
}

export function isAbortError(error: unknown): boolean {
  return error instanceof DOMException && error.name === 'AbortError';
}
